/**
 * profileWriteThrough — one writer for the profile stores (Phase 0 of the
 * org-information build, 2026-08-15 owner report).
 *
 * The /profile page displays users/{uid}/user_profile/user_profile
 * (profiles[0]) for personal fields and the users/{uid} ROOT doc
 * (firm_name / organization_employee_count) for org fields — but until this
 * module, NOTHING in signup, redemption, or onboarding ever wrote either
 * place. Names chosen at signup, the org name/size chosen in onboarding, and
 * the contact phone all landed in stores the page never reads, so the page
 * rendered placeholders (owner screenshot 2026-08-15).
 *
 * Two entry points, one rule — MERGE-IF-EMPTY, never overwrite a user edit:
 *   · writeThroughProfile(fill)  — called at onboarding completion with the
 *     freshly chosen answers.
 *   · readRepairProfile()        — called when /profile loads; back-fills
 *     EXISTING accounts from accounts/{uid}/onboarding/profile + auth
 *     identity the first time they visit, no backfill job needed.
 *
 * The pure merge/mapping logic is exported separately so it is unit-testable
 * without Firebase.
 */

import { getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';

/** Personal + org facts a funnel surface can contribute. All optional. */
export interface ProfileFill {
  firstName?: string;
  lastName?: string;
  /** Contact phone (how we reach the person — NEVER the provisioned Prest0n number). */
  phone?: string;
  /** Organization/business name (onboarding "firm name"). */
  orgName?: string;
  /** Onboarding team-size label, e.g. '2–10 people' — mapped to a band here. */
  teamSizeLabel?: string;
}

/**
 * Onboarding label → ORG_SIZE_BANDS value ('1-4' | '5-19' | '20-39' | '40+').
 * The two vocabularies predate each other on different surfaces; mapped by
 * RANGE MIDPOINT so a straddling label lands in the band most of its range
 * occupies ('2–10 people' → midpoint 6 → '5-19'). Unknown labels map to null
 * — never guess a band that would drive plan recommendations.
 */
export function bandForTeamSizeLabel(label?: string | null): string | null {
  if (!label) return null;
  const s = String(label).toLowerCase();
  if (/just me|only me|solo/.test(s)) return '1-4';
  const nums = (s.match(/\d+/g) || []).map(Number).filter(Number.isFinite);
  if (nums.length === 0) return null;
  const lo = Math.min(...nums);
  const hi = Math.max(...nums);
  // '500+ people' style: single number with a plus — treat as the floor.
  const mid = nums.length === 1 ? lo : (lo + hi) / 2;
  if (mid <= 4) return '1-4';
  if (mid <= 19) return '5-19';
  if (mid <= 39) return '20-39';
  return '40+';
}

/** True for values that should be treated as absent (never overwritten TO, always overwritable FROM). */
function empty(v: unknown): boolean {
  return typeof v !== 'string' || v.trim() === '';
}

/**
 * Pure merge for the profiles[0] personal record: returns the fields that
 * should be written, or null when nothing needs writing. Existing non-empty
 * values always win — auto-fill must never clobber a user edit.
 */
export function mergePersonalFill(
  existing: { first_name?: string; last_name?: string; phone?: string } | null | undefined,
  fill: ProfileFill,
): Record<string, string> | null {
  const out: Record<string, string> = {};
  if (!empty(fill.firstName) && empty(existing?.first_name)) out.first_name = fill.firstName!.trim();
  if (!empty(fill.lastName) && empty(existing?.last_name)) out.last_name = fill.lastName!.trim();
  if (!empty(fill.phone) && empty(existing?.phone)) out.phone = fill.phone!.trim();
  return Object.keys(out).length > 0 ? out : null;
}

/** Pure merge for the users/{uid} ROOT org fields. Same merge-if-empty rule. */
export function mergeOrgFill(
  existing: { firm_name?: string; organization_employee_count?: string } | null | undefined,
  fill: ProfileFill,
): Record<string, string> | null {
  const out: Record<string, string> = {};
  if (!empty(fill.orgName) && empty(existing?.firm_name)) out.firm_name = fill.orgName!.trim();
  const band = bandForTeamSizeLabel(fill.teamSizeLabel);
  if (band && empty(existing?.organization_employee_count)) out.organization_employee_count = band;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Write `fill` through to both stores, merge-if-empty. Safe to call multiple
 * times; every failure is swallowed (the funnel must never break on a
 * profile nicety) but reported to the console for diagnosis.
 */
export async function writeThroughProfile(fill: ProfileFill): Promise<void> {
  try {
    const user = getAuth().currentUser;
    if (!user) return;
    const db = getFirestore();

    // Personal record — profiles[0] of the page's document.
    try {
      const ref = doc(db, 'users', user.uid, 'user_profile', 'user_profile');
      const snap = await getDoc(ref);
      const data: any = snap.exists() ? snap.data() : null;
      const profiles: any[] = Array.isArray(data?.profiles) ? [...data.profiles] : [];
      const current = profiles[0] || {};
      const merged = mergePersonalFill(current, fill);
      if (merged) {
        profiles[0] = { ...current, ...merged };
        await setDoc(ref, { profiles }, { merge: true });
      }
    } catch (e) {
      console.warn('[profileWriteThrough] personal write-through failed', e);
    }

    // Org fields — users/{uid} ROOT doc (the org card + plan recommender read these).
    try {
      const rootRef = doc(db, 'users', user.uid);
      const rootSnap = await getDoc(rootRef);
      const rootData: any = rootSnap.exists() ? rootSnap.data() : null;
      const merged = mergeOrgFill(rootData, fill);
      if (merged) await setDoc(rootRef, merged, { merge: true });
    } catch (e) {
      console.warn('[profileWriteThrough] org write-through failed', e);
    }
  } catch (e) {
    console.warn('[profileWriteThrough] skipped', e);
  }
}

/**
 * Read-repair for accounts that finished onboarding BEFORE the write-through
 * existed: pull what onboarding saved (accounts/{uid}/onboarding/profile)
 * plus auth identity, and run the same merge-if-empty write. Called from the
 * /profile page on load — each existing account self-heals on first visit.
 */
export async function readRepairProfile(identity?: { firstName?: string; lastName?: string }): Promise<void> {
  try {
    const user = getAuth().currentUser;
    if (!user) return;
    const db = getFirestore();
    let onboarding: any = null;
    try {
      const snap = await getDoc(doc(db, 'accounts', user.uid, 'onboarding', 'profile'));
      onboarding = snap.exists() ? snap.data() : null;
    } catch { /* fine — repair from identity alone */ }
    await writeThroughProfile({
      firstName: identity?.firstName,
      lastName: identity?.lastName,
      phone: typeof onboarding?.contactPhone === 'string' ? onboarding.contactPhone : undefined,
      orgName: typeof onboarding?.firmName === 'string' ? onboarding.firmName : undefined,
      teamSizeLabel: typeof onboarding?.firmSize === 'string' ? onboarding.firmSize : undefined,
    });
  } catch (e) {
    console.warn('[profileWriteThrough] read-repair skipped', e);
  }
}
